import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import SchemaOrg from '@/components/feature/SchemaOrg';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, categoryMap } from '@/mocks/categories';
import { getArticlesByCategory, type Article } from '@/mocks/articles';

function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return views.toString();
}

function VideoCard({ article }: { article: Article }) {
  return (
    <Link
      to={`/article/${article.id}`}
      className="group bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-300"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={article.thumbnail}
          alt={article.title}
          title={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        <div className="absolute bottom-3 right-3 bg-black/70 backdrop-blur-sm text-white text-xs px-2 py-0.5 rounded-md">
          {article.duration}
        </div>
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-12 h-12 rounded-full bg-primary-500/90 flex items-center justify-center">
            <i className="ri-play-fill text-white text-xl ml-0.5"></i>
          </div>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-heading text-foreground-900 text-sm leading-snug line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
          {article.title}
        </h3>
        <p className="text-foreground-500 text-xs line-clamp-2 mt-1.5 mb-3">{article.description}</p>
        <div className="flex items-center justify-between text-xs text-foreground-400">
          <div className="flex items-center gap-1">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-eye-line"></i>
            </span>
            {formatViews(article.views)}
          </div>
          <div className="flex items-center gap-1">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-thumb-up-line"></i>
            </span>
            {formatViews(article.likes)}
          </div>
          <span>{article.date}</span>
        </div>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = slug ? categoryMap[slug] : undefined;
  const [activeSlug, setActiveSlug] = useState(slug || 'entertainment');

  const activeCategory = categoryMap[activeSlug];
  const categoryArticles = useMemo(() => getArticlesByCategory(activeSlug), [activeSlug]);

  if (!currentCategory && slug) {
    return (
      <>
        <SEO title="分类未找到" description="抱歉，您访问的分类不存在。" />
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-900 mb-2">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <SEO
        title={`${activeCategory?.name || '分类'} - 视频分类`}
        description={activeCategory?.description || '浏览黄瓜视频的丰富内容分类，发现更多精彩视频'}
        keywords={`黄瓜视频,${activeCategory?.name || ''}视频,短视频,在线观看`}
        canonicalUrl={`https://huangguavideo.com/category/${activeSlug}`}
      />
      <SchemaOrg type="BreadcrumbList" data={{
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://huangguavideo.com' },
          { '@type': 'ListItem', position: 2, name: activeCategory?.name || '分类', item: `https://huangguavideo.com/category/${activeSlug}` },
        ],
      }} />
      <SchemaOrg type="WebPage" data={{
        name: `${activeCategory?.name} - 黄瓜视频`,
        description: activeCategory?.description || '',
        url: `https://huangguavideo.com/category/${activeSlug}`,
        isPartOf: { '@type': 'WebSite', name: '黄瓜视频', url: 'https://huangguavideo.com' },
      }} />

      <ScrollToTop />
      <Navbar />

      <main className="pt-20">
        <div className="max-w-7xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: activeCategory?.name || '分类' },
            ]}
          />

          <div className="px-4 md:px-6 pb-12">
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
                {activeCategory?.name}视频
              </h1>
              <p className="text-foreground-600 text-sm">{activeCategory?.description}</p>
            </div>

            <div className="flex flex-wrap gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap flex items-center gap-1.5 ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className={`${cat.icon} text-xs`}></i>
                  </span>
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {categoryArticles.map((article) => (
                  <VideoCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
                  <i className="ri-video-line text-foreground-300 text-3xl"></i>
                </div>
                <p className="text-foreground-500">该分类暂无视频内容</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}